// Chain Arc lightning (owner 2026-11-02: 'Chain lighting could probably use a vfx upgrade too, should look … more like
// lightning bolts'). Render-only reaction to the sim's 'arc' event (pts = hero, then each foe hit in order). Every hop is a
// crisp, jagged bolt with a hard white core: pre-subdivided into kinks scaled to the hop length, a couple of thinner side
// forks that branch off and die, and a quick re-strike on a slightly different path a few frames later (the flicker that
// makes lightning read as lightning). A white-blue flash pops where each bolt lands. Own Lightning layer (bright gain).
//
// STORM LATTICE (owner 2026-11-03: 'chain lightning evolved seems really underwhelming … more pizzazz … like lightning
// bolts'): evolved hops are thick, pre-jagged bolts with a white-hot core over a wide electric-violet glow ribbon (the
// glow layer follows the Glow setting), branching side forks with sub-forks, and 2 re-strikes along the same channel
// (3 flashes over ~0.15 s; none with Reduce flashing). Every hop ends in an impact: a spark burst (particle density) and a
// brief crackling outline around the struck foe. SKY STRIKE ('skyStrike' sim event): a huge bolt from the sky onto the
// struck foe with branches, re-strikes, ground flash, shock rings and sparks; view adds the scorch, the app the edge flicker.
// All pooled (Lightning segment pools prewarmed); every flash goes through the FLASH limiter.
import * as THREE from 'three/webgpu';
import { Lightning } from './lightning';
import type { Fx } from './fx';
import { FLASH } from './a11y';

interface Pending { pts: number[]; col: number; evo: boolean; t: number }
/** a Storm Lattice re-strike: the stored channel paths (x,y,z triples), redrawn with a little jitter */
interface StormPending { paths: number[][]; col: number; w: number; t: number; k: number }
const R = Math.random;
/** Storm Lattice glow: electric blue-violet under the violet-edged white cores */
const HALO = 0x7c88ff;

export class ChainBolts {
  private bolts: Lightning;
  private pending: Pending[] = [];
  /** QA: stretch bolt lifetimes so a still can catch them (?glowcheck&skills=arc) */
  lifeK = 1;
  /** Storm Lattice: thick white-hot cores, and the wide violet glow ribbon under them (Glow setting) */
  private storm: Lightning;
  private halo: Lightning;
  private stormPending: StormPending[] = [];
  private jit: number[] = [];
  /** Glow setting (view: surge.glowOn) and the particle-density scale (view: fxDensity) */
  glowOn = true;
  density = 1;
  /** QA counters (?arccheck): Storm Lattice hops drawn, Sky Strikes drawn */
  stats = { hops: 0, sky: 0 };
  constructor(scene: THREE.Scene, private fx: Fx, private flash: (x: number, z: number, r: number, col: number) => void) {
    this.bolts = new Lightning(scene, false, 0.34, 1.9);
    this.halo = new Lightning(scene, false, 0, 0.8, 1600, true); // additive soft glow under the cores
    this.storm = new Lightning(scene, false, 0.5, 2.1, 3200);
    this.storm.prewarm(1600); this.halo.prewarm(600);
  }

  /** a chain: x,y,z triples (hero first) */
  strike(pts: number[], col: number, evo: boolean): void {
    if (evo) { this.stormStrike(pts, col); return; }
    this.draw(pts, col, evo, 1);
    if (FLASH.L.restrike) this.pending.push({ pts: pts.slice(), col, evo, t: 0.055 + R() * 0.03 }); // re-strike (a11y: none when flashes are reduced)
    for (let i = 3; i + 2 < pts.length; i += 3) {
      this.fx.stars.add(pts[i], pts[i + 1] + 0.3, pts[i + 2], evo ? 1.6 : 1.3, 0.09, 0xf4f8ff, 2.8);
      this.flash(pts[i], pts[i + 2], evo ? 1.8 : 1.4, col);
      for (let k = 0; k < 3; k++) { const a = R() * 6.283, s = 4 + R() * 4; this.fx.particles.emitDir(pts[i], pts[i + 1] + 0.4, pts[i + 2], Math.cos(a) * s, 2 + R() * 3, Math.sin(a) * s, 0xeaf6ff, 0.22, 0.18, 3); }
    }
  }

  private draw(pts: number[], col: number, evo: boolean, k: number): void {
    const w = (evo ? 0.42 : 0.32) * k;
    for (let i = 0; i + 5 < pts.length; i += 3) {
      const ax = pts[i], ay = pts[i + 1] + 0.6, az = pts[i + 2], bx = pts[i + 3], by = pts[i + 4] + 0.5, bz = pts[i + 5];
      const len = Math.hypot(bx - ax, by - ay, bz - az), n = Math.max(4, Math.min(12, Math.round(len / 0.7)));
      const amp = Math.min(1.2, 0.18 + len * 0.09);
      const path: number[] = [ax, ay, az];
      for (let s = 1; s < n; s++) {
        const f = s / n, env = Math.sin(f * Math.PI); // kinks are biggest mid-hop, pinned at both ends
        path.push(ax + (bx - ax) * f + (R() - 0.5) * 2 * amp * env, ay + (by - ay) * f + (R() - 0.5) * 1.2 * amp * env + Math.sin(f * Math.PI) * 0.6, az + (bz - az) * f + (R() - 0.5) * 2 * amp * env);
      }
      path.push(bx, by, bz);
      this.bolts.add(path, col, w, 0.16 * this.lifeK, 0.22);
      // side forks: thin branches leaving a random kink, heading roughly onward, then dying out
      const forks = evo ? 3 : 2;
      for (let f = 0; f < forks; f++) {
        const j = 1 + Math.floor(R() * (n - 1)), px = path[j * 3], py = path[j * 3 + 1], pz = path[j * 3 + 2];
        const dx = (bx - ax) / len, dz = (bz - az) / len, side = R() < 0.5 ? -1 : 1, fl = 0.8 + R() * Math.min(2.2, len * 0.35);
        const qx = px + (dx * 0.6 - dz * side * 0.8) * fl, qy = py - 0.2 - R() * 0.6, qz = pz + (dz * 0.6 + dx * side * 0.8) * fl;
        this.bolts.add([px, py, pz, (px + qx) / 2 + (R() - 0.5) * 0.4, (py + qy) / 2, (pz + qz) / 2 + (R() - 0.5) * 0.4, qx, qy, qz], col, w * 0.45, 0.1 * this.lifeK, 0.25);
      }
    }
  }

  // ---------- Storm Lattice ----------
  /** a jagged channel a→b: n kinks, biggest mid-hop, pinned at both ends (+ a slight upward bow) */
  private channel(ax: number, ay: number, az: number, bx: number, by: number, bz: number, n: number, amp: number, bow: number): number[] {
    const path: number[] = [ax, ay, az];
    for (let s = 1; s < n; s++) {
      const f = s / n, env = Math.sin(f * Math.PI);
      path.push(ax + (bx - ax) * f + (R() - 0.5) * 2 * amp * env, ay + (by - ay) * f + (R() - 0.5) * amp * env + env * bow, az + (bz - az) * f + (R() - 0.5) * 2 * amp * env);
    }
    path.push(bx, by, bz);
    return path;
  }
  /** side forks off a channel: each leaves a random kink heading roughly onward (dx,dz) and down, and may split once more */
  private forks(path: number[], dx: number, dz: number, count: number, reach: number, col: number, w: number, life: number, out: number[][]): void {
    const n = path.length / 3;
    for (let f = 0; f < count; f++) {
      const j = 1 + Math.floor(R() * (n - 2)), px = path[j * 3], py = path[j * 3 + 1], pz = path[j * 3 + 2];
      const side = R() < 0.5 ? -1 : 1, fl = reach * (0.5 + R() * 0.6);
      const qx = px + (dx * 0.6 - dz * side * 0.8) * fl, qy = py - 0.25 - R() * 0.3 * fl, qz = pz + (dz * 0.6 + dx * side * 0.8) * fl;
      const fork = this.channel(px, py, pz, qx, qy, qz, 3, fl * 0.18, 0);
      this.storm.addRaw(fork, col, w * 0.42, life * 0.75); out.push(fork);
      if (R() < 0.6) { // a sub-fork off the fork
        const m = 3 * (1 + Math.floor(R() * 2)), sx = fork[m], sy = fork[m + 1], sz = fork[m + 2], sl = fl * 0.5;
        const sub = [sx, sy, sz, sx + (dx * 0.5 + dz * side * 0.7) * sl + (R() - 0.5) * 0.4, sy - 0.15 - R() * 0.3, sz + (dz * 0.5 - dx * side * 0.7) * sl + (R() - 0.5) * 0.4];
        this.storm.addRaw(sub, col, w * 0.22, life * 0.6); out.push(sub);
      }
    }
  }
  /** a brief crackling outline around a struck foe: a jagged loop of short arcs + a couple of vertical crackles */
  private outline(x: number, y: number, z: number, col: number, life: number): void {
    const r = 0.62, seg = 8, a0 = R() * 6.283, ring: number[] = [];
    for (let k = 0; k <= seg; k++) { const a = a0 + k / seg * 6.283, rr = r * (0.85 + R() * 0.35); ring.push(x + Math.cos(a) * rr, y + 0.15 + (R() - 0.5) * 0.5, z + Math.sin(a) * rr); }
    this.storm.addRaw(ring, 0xeef4ff, 0.12, life);
    if (this.glowOn) this.halo.addRaw(ring, HALO, 0.34, life);
    for (let k = 0; k < 2; k++) { const a = R() * 6.283, cx = x + Math.cos(a) * r * 0.9, cz = z + Math.sin(a) * r * 0.9; this.storm.addRaw([cx, y - 0.35, cz, cx + (R() - 0.5) * 0.3, y + 0.2, cz + (R() - 0.5) * 0.3, cx, y + 0.75, cz], 0xeef4ff, 0.09, life * 0.8); }
  }
  /** queue the re-strikes of a channel (2 → 3 flashes over ~0.15 s; none with Reduce flashing) */
  private restrike(paths: number[][], col: number, w: number): void {
    if (!FLASH.L.restrike) return;
    this.stormPending.push({ paths, col, w, t: 0.045 + R() * 0.015, k: 0.85 }, { paths, col, w, t: 0.1 + R() * 0.02, k: 0.7 });
  }
  /** Storm Lattice chain: thick cored hops + glow + forks + re-strikes; an impact (sparks, outline, flash) on every foe */
  private stormStrike(pts: number[], col: number): void {
    const life = 0.17 * this.lifeK, w = 0.62, main: number[][] = [], dens = this.density;
    for (let i = 0; i + 5 < pts.length; i += 3) {
      const ax = pts[i], ay = pts[i + 1] + 0.6, az = pts[i + 2], bx = pts[i + 3], by = pts[i + 4] + 0.5, bz = pts[i + 5];
      const len = Math.hypot(bx - ax, by - ay, bz - az) || 0.01, n = Math.max(4, Math.min(9, Math.round(len / 0.9)));
      const path = this.channel(ax, ay, az, bx, by, bz, n, Math.min(1.3, 0.22 + len * 0.1), 0.5);
      this.storm.addRaw(path, col, w, life);
      if (this.glowOn) this.halo.addRaw(path, HALO, w * 2.8, life * 1.15);
      main.push(path);
      this.forks(path, (bx - ax) / len, (bz - az) / len, 2, Math.min(2.6, 0.9 + len * 0.3), col, w, life, main);
      this.stats.hops++;
    }
    this.restrike(main, col, w);
    for (let i = 3; i + 2 < pts.length; i += 3) {
      const x = pts[i], y = pts[i + 1] + 0.5, z = pts[i + 2];
      this.fx.stars.add(x, y, z, 1.9, 0.1, 0xf4f8ff, 3);
      this.flash(x, z, 2.1, col);
      this.outline(x, y, z, col, 0.2 * this.lifeK);
      const sparks = Math.round(5 * dens);
      for (let k = 0; k < sparks; k++) { const a = R() * 6.283, sp = 5 + R() * 5; this.fx.particles.emitDir(x, y, z, Math.cos(a) * sp, 2 + R() * 4, Math.sin(a) * sp, k & 1 ? 0xeaf6ff : 0xc8a8ff, 0.2, 0.22, 3); }
    }
  }
  /** SKY STRIKE: a huge bolt from above onto (x, gy, z) with branches, re-strikes, glow, flash, rings and sparks */
  skyStrike(x: number, gy: number, z: number, r: number, col: number): void {
    this.stats.sky++;
    const top = gy + 20 + R() * 4, life = 0.24 * this.lifeK, w = 1.15, n = 16, paths: number[][] = [];
    let px = x + (R() - 0.5) * 6, pz = z + (R() - 0.5) * 6;
    const path: number[] = [px, top, pz];
    for (let s = 1; s < n; s++) { // a random walk that homes in on the target as it falls (big kinks high, tight near the ground)
      const f = s / n, wob = 1.4 * (1 - f) + 0.2;
      px += (x - px) * (0.18 + f * 0.5) + (R() - 0.5) * 2 * wob; pz += (z - pz) * (0.18 + f * 0.5) + (R() - 0.5) * 2 * wob;
      path.push(px, top + (gy + 0.2 - top) * f + (R() - 0.5) * 0.6, pz);
    }
    path.push(x, gy + 0.2, z);
    this.storm.addRaw(path, col, w, life);
    if (this.glowOn) this.halo.addRaw(path, HALO, w * 4, life * 1.2);
    paths.push(path);
    for (let b = 0; b < 4; b++) { // branches off the upper two-thirds, heading down and out
      const j = 2 + Math.floor(R() * (n * 0.6)), bx = path[j * 3], by = path[j * 3 + 1], bz = path[j * 3 + 2], a = R() * 6.283, L = 3 + R() * 4;
      const br = this.channel(bx, by, bz, bx + Math.cos(a) * L, by - L * (0.8 + R() * 0.6), bz + Math.sin(a) * L, 5, L * 0.15, 0);
      this.storm.addRaw(br, col, w * 0.38, life * 0.8); if (this.glowOn) this.halo.addRaw(br, HALO, w * 1.2, life * 0.8);
      paths.push(br);
      const m = 3 * (2 + Math.floor(R() * 3)), sub = this.channel(br[m], br[m + 1], br[m + 2], br[m] + (R() - 0.5) * 3, br[m + 1] - 1.5 - R() * 1.5, br[m + 2] + (R() - 0.5) * 3, 3, 0.3, 0);
      this.storm.addRaw(sub, col, w * 0.18, life * 0.6); paths.push(sub);
    }
    this.restrike(paths, col, w);
    // impact: ground flash + flash sphere + shock rings + star + sparks + light (all through the FLASH limiter)
    this.flash(x, z, r * 1.9, col);
    this.fx.flashes.add(x, gy + 0.5, z, r * 0.7, 0.2, 0xf0ecff, 1.6);
    this.fx.rings.add(x, gy + 0.08, z, r * 1.7, 0.42, col, 2.2, 1);
    this.fx.rings.add(x, gy + 0.12, z, r * 1.05, 0.26, 0xf4f0ff, 2.6, 1.6);
    this.fx.stars.add(x, gy + 0.8, z, 3.4, 0.14, 0xf8f6ff, 3.2);
    this.fx.lights.flash(x, gy + 1.5, z, col, 34, 0.22, r * 6);
    const sparks = Math.round(16 * this.density);
    for (let k = 0; k < sparks; k++) { const a = R() * 6.283, sp = 4 + R() * 7; this.fx.particles.emitDir(x, gy + 0.3, z, Math.cos(a) * sp, 3 + R() * 6, Math.sin(a) * sp, k % 3 ? 0xe8f2ff : col, 0.22, 0.3 + R() * 0.2, 3); }
    this.fx.particles.emit(x, gy + 0.2, z, Math.round(10 * this.density), 3, 0x6a6080, 0.45, 0.6, 1.5, -2, 2.5, 1); // dust kicked up
    this.fx.shake = Math.min(1, this.fx.shake + 0.3);
  }

  update(dt: number, cam: THREE.Vector3): void {
    for (let i = this.pending.length - 1; i >= 0; i--) {
      const p = this.pending[i]; p.t -= dt;
      if (p.t <= 0) { this.draw(p.pts, p.col, p.evo, 0.8); this.pending.splice(i, 1); }
    }
    for (let i = this.stormPending.length - 1; i >= 0; i--) {
      const p = this.stormPending[i]; p.t -= dt;
      if (p.t > 0) continue;
      const life = 0.13 * this.lifeK;
      for (let k = 0; k < p.paths.length; k++) { // same channel, re-lit with a little jitter (real lightning re-strikes its path)
        const src = p.paths[k], j = this.jit;
        j.length = src.length;
        for (let q = 0; q < src.length; q++) j[q] = src[q] + (q > 2 && q < src.length - 3 ? (R() - 0.5) * 0.25 : 0);
        const main = src.length > 12; // hops / the sky bolt / branches (forks and sub-forks are short)
        this.storm.addRaw(j, p.col, p.w * p.k * (main ? 1 : 0.4), life);
        if (main && this.glowOn) this.halo.addRaw(j, HALO, p.w * p.k * 3, life);
      }
      this.stormPending[i] = this.stormPending[this.stormPending.length - 1]; this.stormPending.pop();
    }
    this.bolts.update(dt, cam); this.storm.update(dt, cam); this.halo.update(dt, cam);
  }
  clear(): void { this.pending.length = 0; this.stormPending.length = 0; this.bolts.clear(); this.storm.clear(); this.halo.clear(); }
}
